/**
 * Resume Search — contextual search across a client account's stored resumes.
 *
 * TWO THINGS ON THIS SCREEN ARE NOT DECORATION.
 *
 * 1. THE SEARCH MODE BANNER. When llmgateway cannot be reached the service answers from stored
 *    document text and says so. Rendering that answer as though it were semantic would make the
 *    misses look like genuine absences — a recruiter would conclude the candidate is not in the
 *    system rather than that the index is down, and those lead to opposite next actions.
 *
 * 2. THE INDEX GAP. "38 documents, 29 searchable" is the number that explains a thin result set.
 *    Without it, nine files that failed to index are indistinguishable from nine candidates who do
 *    not match.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import { EmptyState, ErrorState, Spinner, Tag } from '../components/ui/primitives'
import type { DocumentSearchResponse, IndexHealth } from '../types/tf_types'

const CORPORA: Array<{ value: string; label: string; hint: string }> = [
  { value: 'tf-resumes', label: 'Resumes', hint: 'Candidate resumes, CVs and certifications' },
  { value: 'tf-documents', label: 'Documents', hint: 'Proposals and other attachments' },
]

export function ResumeSearchPage() {
  const { notifyError } = useApp()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') ?? '')
  const [corpus, setCorpus] = useState('tf-resumes')
  const [result, setResult] = useState<DocumentSearchResponse | null>(null)
  const [health, setHealth] = useState<IndexHealth | null>(null)
  const [loading, setLoading] = useState(false)
  const [healthError, setHealthError] = useState<string | null>(null)

  const loadHealth = useCallback(async () => {
    try {
      setHealth(await tfApi.fetchIndexHealth())
      setHealthError(null)
    } catch (err: any) {
      setHealthError(err?.detail?.message ?? String(err))
    }
  }, [])

  useEffect(() => {
    void loadHealth()
  }, [loadHealth])

  const run = useCallback(
    async (q: string, c: string) => {
      if (!q.trim()) return
      setLoading(true)
      try {
        setResult(await tfApi.searchDocuments(q, c))
      } catch (err) {
        notifyError('The search could not run', err)
      } finally {
        setLoading(false)
      }
    },
    [notifyError],
  )

  useEffect(() => {
    const initial = params.get('q')
    if (initial) void run(initial, corpus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const unsearchable = health ? health.TotalDocuments - health.Searchable : 0

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Talent / Resume Search"
        title="Resume Search"
        description="Search what candidates actually wrote, not the fields somebody remembered to fill in. Every result shows the passage it matched."
        actions={
          <button className="btn secondary" type="button" onClick={() => void loadHealth()}>
            <span className="material-symbols-outlined">refresh</span>Refresh index state
          </button>
        }
      />

      <div className="body-scroll">
        {/* ---------------------------------------------------------------- index state */}
        {healthError && (
          <ErrorState
            title="Could not read the index state"
            message={healthError}
            onRetry={loadHealth}
          />
        )}

        {health && (
          <div className={`banner ${health.Gateway.Reachable ? 'info' : 'warn'}`}>
            <span className="material-symbols-outlined">
              {health.Gateway.Reachable ? 'database' : 'cloud_off'}
            </span>
            <div>
              <b>
                {health.TotalDocuments} document{health.TotalDocuments === 1 ? '' : 's'} stored ·{' '}
                {health.Searchable} searchable
              </b>
              {unsearchable > 0 && (
                <>
                  {' '}
                  · <b style={{ color: 'var(--tf-rel-blocked-ink, inherit)' }}>{unsearchable} not
                  searchable</b>
                </>
              )}
              <div style={{ marginTop: 4 }}>
                {health.Gateway.Reachable
                  ? 'The document index is available, so search matches meaning rather than words.'
                  : health.Gateway.Detail ??
                    'The document index is not available. Search will match words rather than meaning.'}
              </div>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- the query */}
        <form
          className="ask-bar"
          style={{ marginBottom: 12 }}
          onSubmit={(e) => {
            e.preventDefault()
            setParams({ q: query })
            void run(query, corpus)
          }}
        >
          <span className="material-symbols-outlined" style={{ color: 'var(--tf-accent)' }}>
            manage_search
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search resumes"
            placeholder="Senior data engineer with Databricks and healthcare claims experience"
          />
          <select
            className="ie-select"
            value={corpus}
            aria-label="Which documents to search"
            onChange={(e) => {
              setCorpus(e.target.value)
              if (result) void run(query, e.target.value)
            }}
          >
            {CORPORA.map((c) => (
              <option key={c.value} value={c.value} title={c.hint}>
                {c.label}
              </option>
            ))}
          </select>
          <button className="btn primary sm" type="submit">
            Search
          </button>
        </form>

        {/* ---------------------------------------------------------------- results */}
        {loading && <Spinner />}

        {!loading && result && result.SearchMode === 'TEXT_FALLBACK' && (
          <div className="banner warn">
            <span className="material-symbols-outlined">search_off</span>
            <div>
              <b>These are keyword results, not contextual ones.</b>
              <div style={{ marginTop: 4 }}>{result.SearchModeReason}</div>
              <div style={{ marginTop: 4 }}>
                A candidate who wrote “Delta Lake” will not appear for “Databricks” until the index
                is back. Treat a thin result set as unknown rather than as an answer.
              </div>
            </div>
          </div>
        )}

        {!loading && result && result.Items.length === 0 && (
          <EmptyState
            icon="search_off"
            title="No documents matched"
            message={
              result.SearchMode === 'TEXT_FALLBACK'
                ? `Nothing in the stored text matched “${result.Query}”. With the index unavailable this is not a reliable "no".`
                : `Nothing matched “${result.Query}”.`
            }
          />
        )}

        {!loading &&
          result &&
          result.Items.map((hit) => (
            <div className="card" style={{ marginBottom: 11 }} key={`${hit.DocumentId}-${hit.Passage?.slice(0, 12)}`}>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div className="person-cell">
                      <span className="avatar">
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                          description
                        </span>
                      </span>
                      <div>
                        <Link
                          className="person-name"
                          to={`/relationships/people/${hit.SubjectId}`}
                          style={{ fontSize: 'var(--fs-md)' }}
                        >
                          {hit.FileName}
                        </Link>
                        <div className="person-sub">
                          <Tag>{hit.DocumentType}</Tag>{' '}
                          {hit.SubjectType === 'PERSON' ? 'Contact ' : ''}
                          {hit.SubjectId}
                        </div>
                      </div>
                    </div>

                    {/* The passage is the point. A score with no text asks a recruiter to trust a
                        ranking over their own candidates. */}
                    {hit.Passage && (
                      <blockquote
                        style={{
                          marginTop: 9,
                          padding: '9px 12px',
                          borderLeft: '3px solid var(--tf-300)',
                          background: 'var(--tf-50)',
                          fontSize: 'var(--fs-sm)',
                          lineHeight: 'var(--lh-relaxed)',
                          borderRadius: '0 6px 6px 0',
                        }}
                      >
                        {hit.Passage}
                      </blockquote>
                    )}
                  </div>

                  <div style={{ textAlign: 'right', minWidth: 110 }}>
                    {hit.Score !== null && hit.Score !== undefined && (
                      <div>
                        <div className="triad-label">Match</div>
                        <b style={{ color: 'var(--tf-brand)', fontSize: 'var(--fs-lg)' }}>
                          {(Number(hit.Score) <= 1 ? Number(hit.Score) * 100 : Number(hit.Score)).toFixed(0)}
                        </b>
                      </div>
                    )}
                    <div className="person-sub" style={{ marginTop: 6 }}>
                      {hit.IndexStatus}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* ---------------------------------------------------------------- what failed */}
        {health && health.Failures.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head">
              <span className="card-title">
                <span className="material-symbols-outlined">report</span>
                Stored but not searchable ({health.Failures.length})
              </span>
            </div>
            <div className="card-body">
              <p className="person-sub" style={{ marginBottom: 10 }}>
                These uploaded successfully and will never appear in a search until they are
                indexed. A scanned PDF with no text layer does exactly this.
              </p>
              <div className="table-scroll">
                <table className="dt">
                  <caption className="sr-only">Documents that are stored but not searchable, with the reason.</caption>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Contact</th>
                      <th>State</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.Failures.map((f) => (
                      <tr key={f.DocumentId}>
                        <td>
                          <span className="person-name">{f.FileName}</span>
                        </td>
                        <td>
                          <Link className="person-sub" to={`/relationships/people/${f.SubjectId}`}>
                            Contact {f.SubjectId}
                          </Link>
                        </td>
                        <td>
                          <span className="badge b-blocked">
                            <span className="dot" />
                            {f.IndexStatus}
                          </span>
                        </td>
                        <td>
                          <span className="person-sub">{f.IndexStatusDetail ?? '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
